import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import campaignContext from './campaignContext';

// Configuración dinámica de la URL de la API
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.origin.includes('localhost') ? 'http://localhost:8080/api' : `${window.location.origin}/api`);

const api = axios.create({
  baseURL: API_URL,
  timeout: 5000, // Timeout corto para no bloquear la experiencia
});

// Tope de tiempo reportado para una sola página, en segundos. Coincide con
// services.MaxTimeOnPageSeconds del backend, que también recorta por su
// cuenta: cualquier medida por encima es una pestaña olvidada, no lectura.
const MAX_TIME_ON_PAGE = 30 * 60;

// Tiempo sin teclear tras el cual una búsqueda se considera terminada y se
// reporta. Es más largo que el rebote con el que se piden los resultados
// (que debe sentirse inmediato) porque aquí interesa el término final, no
// cada fragmento por el que pasó el usuario al escribirlo.
const SEARCH_SETTLE_MS = 1200;

// Por debajo de esto un término no identifica nada: encaja con medio catálogo.
// Coincide con minLongitudTerminoBusqueda del backend, que lo usa para
// atribuir búsquedas a un producto.
const MIN_SEARCH_TERM = 3;

/**
 * Puntos del sitio desde los que se puede agregar al carrito o comprar.
 *
 * Es el espejo de commerceSourcesValidos en
 * BackEnd/internal/services/analytics_writer.go: cualquier valor fuera de esta
 * lista se guarda como 'desconocido'. Si añades uno aquí, añádelo también allí.
 */
export const COMMERCE_SOURCES = {
  CATALOGO: 'catalogo',
  BUSCADOR: 'buscador',
  FICHA_PRODUCTO: 'ficha_producto',
  HOME_DESTACADOS: 'home_destacados',
  HOME_DESTACADO_PRINCIPAL: 'home_destacado_principal',
  HOME_NOVEDADES: 'home_novedades',
  HOME_BANNER: 'home_banner',
  AHILESDEJO: 'ahilesdejo',
  CARRITO: 'carrito',
  CHECKOUT: 'checkout',
};

class AnalyticsService {
  constructor() {
    this.sessionId = this.getOrCreateSessionId();
    this.visitorId = this.getOrCreateVisitorId();
    // Registra la llegada por un enlace de campaña, si la hay. A partir de acá
    // el contexto se lee del almacén en cada golpe (ver campaignPayload).
    //
    // De dónde salió la atribución se decide UNA vez, al cargar la página, y no
    // en cada golpe: después CampaignParamsKeeper devuelve los parámetros a la
    // URL, y a partir de ahí toda lectura los vería y diría 'click'.
    //   click    = se llegó con los UTM en la URL (la definición estricta)
    //   restored = los UTM se recuperaron del navegador dentro de los 30 días
    // Sin esta distinción, activar la ventana de atribución subiría las visitas
    // por campaña de un día para otro y parecería que la campaña despegó.
    const llegadaConUTM = campaignContext.parseParams(window.location.search).hayUTM;
    const campania = campaignContext.capture();
    this.attributionKind = campania ? (llegadaConUTM ? 'click' : 'restored') : null;
    this.pageStartTime = Date.now();
    // URL de la página cuyo tiempo se está midiendo. Se necesita aparte de
    // window.location porque el reporte de salida en una SPA ocurre cuando
    // la ruta YA cambió — usar location ahí atribuiría el tiempo de la
    // página anterior a la nueva. null = todavía no hay página que cerrar.
    this.currentPageUrl = null;
    this.isTrackingEnabled = true;
    this.searchSettleTimer = null;
  }

  /**
   * Obtiene o crea un ID de sesión único (se pierde al cerrar la pestaña)
   */
  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }

  /**
   * Session ID actual — el checkout lo adjunta al crear el pago para
   * atribución de campañas. El backend resuelve el UTM correspondiente
   * consultando analytics_sessions por este ID (ver resolveOrderAttribution
   * en payment_controller.go); no hace falta reenviar el UTM aquí.
   */
  getSessionId() {
    return this.sessionId;
  }

  /**
   * Obtiene o crea un ID de visitante persistente en localStorage, sin
   * expiración — a diferencia del session_id, sobrevive a cerrar la
   * pestaña, lo que permite distinguir visitantes únicos de sesiones.
   */
  getOrCreateVisitorId() {
    let visitorId = localStorage.getItem('analytics_visitor_id');
    if (!visitorId) {
      visitorId = uuidv4();
      localStorage.setItem('analytics_visitor_id', visitorId);
    }
    return visitorId;
  }

  /**
   * Campos de campaña que se adjuntan a TODOS los golpes: utm_* y click_id.
   *
   * Antes el UTM viajaba una sola vez, en el primer pageview. Como los golpes
   * salen por colas distintas de RabbitMQ y no hay garantía de orden, si un
   * evento de comercio llegaba antes que ese pageview la sesión se creaba sin
   * campaña y no había forma de recuperarla. Mandarlo en todos los golpes hace
   * que el orden deje de importar: el backend solo rellena lo que está vacío,
   * así que el primer contacto se conserva igual.
   */
  campaignPayload() {
    const payload = campaignContext.toPayload(campaignContext.get());
    if (this.attributionKind && Object.keys(payload).length) {
      payload.attribution_kind = this.attributionKind;
    }
    return payload;
  }

  /**
   * Identificador del clic de campaña, o null. Lo adjunta paymentService al
   * crear el pago para que el pedido quede atado al enlace concreto.
   */
  getClickId() {
    return campaignContext.get()?.click_id || null;
  }

  /**
   * Habilita o deshabilita el tracking
   */
  setTrackingEnabled(enabled) {
    this.isTrackingEnabled = enabled;
  }

  /**
   * Trackea un evento genérico
   */
  async trackEvent(eventType, eventCategory, eventAction, eventLabel = null, metadata = null) {
    if (!this.isTrackingEnabled) return;

    try {
      const eventData = {
        session_id: this.sessionId,
        visitor_id: this.visitorId,
        event_type: eventType,
        event_category: eventCategory,
        event_action: eventAction,
        event_label: eventLabel,
        page_url: window.location.pathname,
        page_title: document.title,
        referrer: document.referrer || null,
        metadata: metadata,
        ...this.campaignPayload(),
      };

      // Enviar en background sin esperar respuesta
      api.post('/analytics/event', eventData).catch(err => {
        console.warn('Analytics tracking failed:', err);
      });
    } catch (error) {
      console.warn('Analytics error:', error);
    }
  }

  /**
   * Trackea una vista de página
   */
  async trackPageView(pageTitle = null) {
    if (!this.isTrackingEnabled) return;

    this.pageStartTime = Date.now();
    this.currentPageUrl = window.location.pathname;

    try {
      const pageViewData = {
        session_id: this.sessionId,
        visitor_id: this.visitorId,
        event_type: 'pageview',
        event_category: 'engagement',
        event_action: 'view',
        page_url: window.location.pathname,
        page_title: pageTitle || document.title,
        referrer: document.referrer || null,
        ...this.campaignPayload(),
      };

      api.post('/analytics/pageview', pageViewData).catch(err => {
        console.warn('PageView tracking failed:', err);
      });
    } catch (error) {
      console.warn('PageView error:', error);
    }
  }

  /**
   * Trackea una búsqueda
   */
  async trackSearch(searchTerm, resultsCount = 0, clickedResult = false) {
    if (!this.isTrackingEnabled) return;

    try {
      const searchData = {
        session_id: this.sessionId,
        visitor_id: this.visitorId,
        search_term: searchTerm,
        results_count: resultsCount,
        clicked_result: clickedResult,
        ...this.campaignPayload(),
      };

      api.post('/analytics/search', searchData).catch(err => {
        console.warn('Search tracking failed:', err);
      });
    } catch (error) {
      console.warn('Search tracking error:', error);
    }
  }

  /**
   * Registra una búsqueda solo cuando el usuario ha terminado de escribirla.
   *
   * El buscador consulta la API con un rebote corto para que los resultados se
   * sientan inmediatos, pero registrar cada uno de esos rebotes llena la tabla
   * de fragmentos: una sola búsqueda de "Psicologo" quedó guardada como cinco
   * ("Ps", "Psi", "Psicolo", "Psicologo"...), lo que infla el número de
   * búsquedas y ensucia el informe de términos más buscados.
   *
   * Aquí se espera SEARCH_SETTLE_MS sin teclear antes de reportar, así que solo
   * se guarda el término final. Y se descartan los términos de menos de
   * MIN_SEARCH_TERM caracteres, que no dicen nada.
   */
  trackSearchSettled(searchTerm, resultsCount = 0, clickedResult = false) {
    if (!this.isTrackingEnabled) return;

    clearTimeout(this.searchSettleTimer);

    const termino = (searchTerm || '').trim();
    if (termino.length < MIN_SEARCH_TERM) return;

    // Al elegir una sugerencia no se espera: la intención ya es inequívoca y
    // el usuario está a punto de irse de la página.
    if (clickedResult) {
      this.trackSearch(termino, resultsCount, true);
      return;
    }

    this.searchSettleTimer = setTimeout(() => {
      this.trackSearch(termino, resultsCount, false);
    }, SEARCH_SETTLE_MS);
  }

  /** Cancela un reporte de búsqueda pendiente (p. ej. al cerrar el buscador). */
  cancelPendingSearch() {
    clearTimeout(this.searchSettleTimer);
  }

  /**
   * Nombre del producto, tolerando las tres formas en que llega según de
   * dónde venga el objeto: normalizeProduct() del catálogo expone Juego,
   * juego y nombre, y las líneas del carrito (CartContext) usan name.
   *
   * Antes se leía product.Nombre —con mayúscula— que no lo produce ninguna
   * de las tres, así que TODOS los eventos de comercio se guardaban con
   * product_name en NULL y el panel de "productos agregados al carrito",
   * que filtra por nombre no nulo, salía siempre vacío.
   */
  productName(product) {
    return (
      product?.Juego ||
      product?.juego ||
      product?.nombre ||
      product?.Nombre ||
      product?.name ||
      null
    );
  }

  /**
   * Precio unitario del producto, con las mismas variantes de nombre que
   * productName(). Las líneas del carrito traen `price` ya resuelto (oferta
   * si aplica); el catálogo trae Precio_Publico/precio_publico.
   */
  productPrice(product) {
    return (
      product?.Precio_Publico ??
      product?.precio_publico ??
      product?.price ??
      null
    );
  }

  /**
   * Trackea eventos de comercio
   */
  async trackCommerce(eventType, product = null, quantity = 1, totalAmount = null, source = null) {
    if (!this.isTrackingEnabled) return;

    try {
      const commerceData = {
        session_id: this.sessionId,
        visitor_id: this.visitorId,
        // add_to_cart, buy_now, remove_from_cart, view_cart, view_product,
        // start_checkout, complete_checkout, abandon_cart
        event_type: eventType,
        // Punto del sitio desde el que se disparó (ver COMMERCE_SOURCES). El
        // backend lo valida contra la misma lista y degrada lo desconocido a
        // 'desconocido', así que una errata aquí no ensucia el panel.
        source: source || null,
        product_id: product?.id || product?.productId || null,
        product_name: this.productName(product),
        product_price: this.productPrice(product),
        quantity: quantity,
        total_amount: totalAmount,
        ...this.campaignPayload(),
      };

      api.post('/analytics/commerce', commerceData).catch(err => {
        console.warn('Commerce tracking failed:', err);
      });
    } catch (error) {
      console.warn('Commerce tracking error:', error);
    }
  }

  /**
   * Trackea tiempo en página al salir de ella: cambio de ruta, cambio de
   * pestaña o cierre. Va por navigator.sendBeacon (más confiable que
   * beforeunload, especialmente en móvil — beforeunload no siempre dispara
   * ahí). Reinicia pageStartTime para que el próximo reporte mida solo el
   * tiempo incremental, no el acumulado desde el primer ingreso.
   *
   * Solo cuenta tiempo con la pestaña visible: quien vuelve a una pestaña
   * que dejó abierta reinicia el cronómetro (ver initAutoTracking). Antes no
   * era así y el tiempo en segundo plano se sumaba entero — el promedio
   * histórico llegó a 3.618 s por página, con máximos de 39 horas.
   */
  trackPageExit() {
    if (!this.isTrackingEnabled) return;
    // Nada que cerrar: aún no se ha registrado ninguna página.
    if (!this.currentPageUrl) return;

    const elapsed = Math.floor((Date.now() - this.pageStartTime) / 1000);
    const pageUrl = this.currentPageUrl;
    this.pageStartTime = Date.now();
    if (elapsed <= 0) return;

    const payload = {
      session_id: this.sessionId,
      visitor_id: this.visitorId,
      page_url: pageUrl,
      duration_seconds: Math.min(elapsed, MAX_TIME_ON_PAGE),
      ...this.campaignPayload(),
    };

    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${API_URL}/analytics/timing`, blob);
    } else {
      api.post('/analytics/timing', payload).catch((err) => {
        console.warn('Timing tracking failed:', err);
      });
    }
  }

  /**
   * Trackea clicks en elementos
   */
  trackClick(elementType, elementLabel, metadata = null) {
    this.trackEvent('interaction', 'click', elementType, elementLabel, metadata);
  }

  /**
   * Trackea errores
   */
  trackError(errorType, errorMessage, metadata = null) {
    this.trackEvent('error', errorType, 'error_occurred', errorMessage, metadata);
  }

  /**
   * Trackea conversiones
   */
  trackConversion(conversionType, value = null, metadata = null) {
    this.trackEvent('conversion', conversionType, 'convert', null, {
      value: value,
      ...metadata,
    });
  }

  /**
   * Métricas específicas de producto
   */

  /**
   * Registra la llegada a la página de producto.
   *
   * Escribe en las dos tablas a propósito, porque cada una responde una
   * pregunta distinta y hay consultas vivas apuntando a cada lado:
   *  - analytics_events ('product_detail') es de donde lee el paso 2 del
   *    embudo del panel.
   *  - analytics_commerce ('view_product') es de donde leen las métricas de
   *    rendimiento y conversión por producto. Estuvieron devolviendo 0 vistas
   *    y 0% de conversión durante meses porque nadie escribía ese evento.
   *
   * Ojo: llamarlo una sola vez por visita. React StrictMode ejecuta los
   * efectos dos veces en desarrollo, así que quien lo invoque debe protegerse
   * (ProductDetailPage usa un useRef).
   */
  trackProductPageView(product, source = null) {
    this.trackEvent('product', 'view', 'product_detail', this.productName(product), {
      product_id: product.id,
      product_type: product.Tipo,
      product_price: this.productPrice(product),
      has_offer: product.Oferta === 'Si',
      source: source || null,
    });
    this.trackCommerce('view_product', product, 1, null, source);
  }

  /** @deprecated Usar trackProductPageView. Se mantiene por compatibilidad. */
  trackProductView(product) {
    this.trackProductPageView(product);
  }

  trackProductAddToCart(product, quantity = 1, source = null) {
    this.trackCommerce('add_to_cart', product, quantity, this.productPrice(product) * quantity, source);
    this.trackEvent('product', 'add_to_cart', 'cart', this.productName(product), {
      product_id: product.id,
      quantity: quantity,
      source: source || null,
    });
  }

  /**
   * "Comprar ahora": es un evento propio y NO se emite además como
   * add_to_cart. Si se emitieran los dos, los conteos de add_to_cart
   * absorberían en silencio las compras directas y las dos acciones nunca
   * podrían separarse — que es justo lo que se quiere medir.
   */
  trackProductBuyNow(product, quantity = 1, source = null) {
    this.trackCommerce('buy_now', product, quantity, this.productPrice(product) * quantity, source);
    this.trackEvent('product', 'buy_now', 'cart', this.productName(product), {
      product_id: product.id,
      quantity: quantity,
      source: source || null,
    });
  }

  trackProductRemoveFromCart(product) {
    this.trackCommerce('remove_from_cart', product);
    this.trackEvent('product', 'remove_from_cart', 'cart', this.productName(product), {
      product_id: product.id,
    });
  }

  /**
   * Métricas de carrito
   */
  trackCartView(cart) {
    this.trackCommerce('view_cart', null, cart.items?.length || 0, cart.total, COMMERCE_SOURCES.CARRITO);
    this.trackEvent('cart', 'view', 'view_cart', null, {
      items_count: cart.items?.length || 0,
      total_amount: cart.total,
    });
  }

  trackCheckoutStart(cart) {
    this.trackCommerce('start_checkout', null, cart.items?.length || 0, cart.total);
    this.trackEvent('checkout', 'start', 'checkout_initiated', null, {
      items_count: cart.items?.length || 0,
      total_amount: cart.total,
    });
  }

  trackCheckoutComplete(orderId, totalAmount, items) {
    this.trackCommerce('complete_checkout', null, items?.length || 0, totalAmount);
    this.trackConversion('purchase', totalAmount, {
      order_id: orderId,
      items_count: items?.length || 0,
    });
  }

  trackCartAbandonment() {
    this.trackCommerce('abandon_cart');
    this.trackEvent('cart', 'abandon', 'cart_abandoned');
  }

  /**
   * Métricas de usuario
   */
  trackLogin(method = 'email') {
    this.trackEvent('user', 'login', 'login_success', method);
  }

  trackLogout() {
    this.trackEvent('user', 'logout', 'logout');
  }

  trackRegistration(method = 'email') {
    this.trackConversion('registration', null, { method: method });
    this.trackEvent('user', 'registration', 'register_success', method);
  }

  /**
   * Métricas de navegación
   */
  trackNavigation(destination, source) {
    this.trackEvent('navigation', 'navigate', destination, source);
  }

  trackFilterChange(filterType, filterValue) {
    this.trackEvent('filter', 'change', filterType, filterValue);
  }

  trackSortChange(sortBy) {
    this.trackEvent('sort', 'change', 'sort_products', sortBy);
  }

  /**
   * Inicializar tracking automático.
   *
   * No registra el pageview inicial: de eso se encarga <AnalyticsTracker/>,
   * que ya dispara en el montaje y en cada cambio de ruta. Hacerlo también
   * aquí duplicaba el primer pageview de cada sesión —casi la mitad de los
   * pageviews almacenados son ese duplicado— e inflaba page_views por
   * sesión, que es la base de la tasa de rebote.
   */
  initAutoTracking() {
    // Track tiempo en página: visibilitychange + pagehide, no beforeunload
    // (poco confiable en móvil, sobre todo Safari/iOS).
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.trackPageExit();
      } else {
        // De vuelta en la pestaña: el cronómetro arranca de cero para no
        // contar como lectura el rato que estuvo en segundo plano.
        this.pageStartTime = Date.now();
      }
    });
    window.addEventListener('pagehide', () => {
      this.trackPageExit();
    });

    // Track errores globales
    window.addEventListener('error', (event) => {
      this.trackError('javascript_error', event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });

    // Track errores de promesas no manejadas
    window.addEventListener('unhandledrejection', (event) => {
      this.trackError('unhandled_promise_rejection', event.reason?.message || 'Promise rejected', {
        reason: event.reason,
      });
    });
  }
}

// Exportar instancia singleton
const analyticsService = new AnalyticsService();
export default analyticsService;
